import { computed, ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useColorRenderDebounce -- shared read of the SuperAdmin
 * ``color_render_debounce_seconds`` platform setting: how long the product
 * editor waits after the LAST save before it renders newly selected colors.
 *
 * Mirrors useHideUnpreviewableColors: one GET per page load (module-level
 * cache + in-flight dedupe), shared by every caller.
 *
 * Fail-SAFE default: the delay reads DEFAULT_COLOR_RENDER_DEBOUNCE_SECONDS
 * (5, the backend's unset default) while the fetch is in flight AND when it
 * errors or returns a bad shape. A read failure must never leave the editor
 * with no delay (every toggle would spend a render) or an absurd one: the
 * default is the value an untouched platform runs on.
 *
 * Public surface:
 *
 *   const { debounceSeconds, debounceMs, loading, error } = useColorRenderDebounce()
 *
 *   debounceSeconds: Ref<number>
 *   debounceMs:      ComputedRef<number>   // read it when you ARM a timer
 *   loading:         Ref<boolean>
 *   error:           Ref<Error|null>
 */
// The /api-prefixed form, like the peer platform-settings reads.
const ENDPOINT = '/api/platform-settings/color-render-debounce-seconds'

// Mirrors the backend's default (api/src/endpoints/platform_settings.py,
// COLOR_RENDER_DEBOUNCE_DEFAULT). The backend also serves it as ``default``.
export const DEFAULT_COLOR_RENDER_DEBOUNCE_SECONDS = 5

const _seconds = ref(DEFAULT_COLOR_RENDER_DEBOUNCE_SECONDS)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

// The backend's allowed range (COLOR_RENDER_DEBOUNCE_MIN / _MAX). The client
// re-checks it so a malformed payload can never produce a ~0 delay (a render
// per keystroke) or one that overflows setTimeout's 32-bit limit; either
// falls back to the default instead.
const MIN_SECONDS = 1
const MAX_SECONDS = 120

function _usable(raw) {
    return (
        typeof raw === 'number' &&
        Number.isFinite(raw) &&
        raw >= MIN_SECONDS &&
        raw <= MAX_SECONDS
    )
}

function _fetchOnce() {
    if (_resolved) {
        return Promise.resolve(_seconds.value)
    }
    if (_inflight) {
        return _inflight
    }
    _loading.value = true
    _error.value = null
    // The leading .then also turns a synchronous throw from the client into
    // the same fail-safe path as a rejected request.
    _inflight = Promise.resolve()
        .then(() => axiosInstance.get(ENDPOINT))
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            _seconds.value = _usable(raw)
                ? raw
                : DEFAULT_COLOR_RENDER_DEBOUNCE_SECONDS
            _resolved = true
            return _seconds.value
        })
        .catch((err) => {
            // Fail SAFE; do NOT mark resolved so a later mount can retry.
            _error.value = err
            _seconds.value = DEFAULT_COLOR_RENDER_DEBOUNCE_SECONDS
            return _seconds.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/** Test-only: reset the module-level shared state between specs. */
export function _resetColorRenderDebounceCache() {
    _seconds.value = DEFAULT_COLOR_RENDER_DEBOUNCE_SECONDS
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useColorRenderDebounce() {
    _fetchOnce()
    const debounceMs = computed(() => Math.round(_seconds.value * 1000))
    return {
        debounceSeconds: _seconds,
        debounceMs,
        loading: _loading,
        error: _error,
    }
}

export default useColorRenderDebounce
